<script setup lang="ts">
import { ref, computed } from 'vue';
import { iSelect, iSelectMulti } from '@inscribe/signal';
import {
  type BaseFieldFilter,
  type FilterOption,
  TransactionBaseFieldFilterOperator,
} from '@/types/decision-engine/configurable-insights.types';
import FilterEditorLayout from '@/V2/decision-engine/configurable-insights/components/filters/FilterEditorLayout.vue';

interface Props {
  filter: BaseFieldFilter | null;
  label: string;
  placeholder?: string;
  options?: FilterOption[];
}

interface Emits {
  (e: 'update:filter', value: BaseFieldFilter): void;
  (e: 'remove'): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select options...',
  options: undefined,
});
const emit = defineEmits<Emits>();

const operatorOptions = [
  { text: 'Is one of', value: TransactionBaseFieldFilterOperator.IS_ONE_OF },
  {
    text: 'Is not one of',
    value: TransactionBaseFieldFilterOperator.IS_NOT_ONE_OF,
  },
];

const currentFilter = computed<BaseFieldFilter>(() => {
  const filter = props.filter || {
    operator: TransactionBaseFieldFilterOperator.IS_ONE_OF,
    values: [],
  };

  return {
    ...filter,
    values: Array.isArray(filter.values)
      ? filter.values.filter(
          (v): v is string => v != null && typeof v === 'string',
        )
      : [],
  };
});

// For free-text mode: seed options from existing values so editing pre-saved filters works correctly
const freeTextOptions = ref<FilterOption[]>(
  props.options === undefined
    ? (props.filter?.values ?? []).map((v) => ({
        value: String(v),
        text: String(v),
      }))
    : [],
);

const handleOptionCreated = (option: { value: string; text: string }) => {
  freeTextOptions.value = [
    ...freeTextOptions.value,
    { value: option.value, text: option.text },
  ];
};

const updateOperator = (operator: TransactionBaseFieldFilterOperator) => {
  emit('update:filter', { ...currentFilter.value, operator });
};

const updateValues = (values: string[]) => {
  emit('update:filter', { ...currentFilter.value, values });
};
</script>

<template>
  <FilterEditorLayout :label="label" @remove="emit('remove')">
    <div class="filter-section">
      <i-select
        :model-value="currentFilter.operator"
        :options="operatorOptions"
        @update:model-value="updateOperator"
      />
    </div>

    <div class="filter-section">
      <i-select-multi
        :model-value="currentFilter.values as string[]"
        :options="options ?? freeTextOptions"
        :allow-create="!options"
        :placeholder="placeholder"
        @update:model-value="updateValues"
        @option-created="handleOptionCreated"
      />
    </div>
  </FilterEditorLayout>
</template>
